import React, { useEffect } from "react";

/**
 * Thermal-printer receipt component (~80mm width).
 * Renders an A8-ish printable area. CSS is scoped via `print:` and a print stylesheet
 * embedded inline so this works regardless of host print CSS.
 *
 * Usage:
 *   <PrintReceipt invoice={inv} autoPrint />
 * The component auto-triggers window.print() when `autoPrint` is true.
 */
export default function PrintReceipt({ invoice, branch, autoPrint = false }) {
  useEffect(() => {
    if (!autoPrint || !invoice) return;
    const t = setTimeout(() => window.print(), 300);
    return () => clearTimeout(t);
  }, [autoPrint, invoice]);

  if (!invoice) return null;
  const items = invoice.items || [];
  const inr = (n) => `Rs ${Number(n || 0).toFixed(2)}`;
  const dt = invoice.created_at ? new Date(invoice.created_at).toLocaleString() : "";

  return (
    <>
      <style>{`
        @media print {
          @page { size: 80mm auto; margin: 4mm; }
          html, body { background: white !important; }
          body * { visibility: hidden !important; }
          #print-receipt, #print-receipt * { visibility: visible !important; }
          #print-receipt { position: absolute; left: 0; top: 0; width: 72mm; }
        }
        @media screen {
          /* Hidden on screen — only shown when window.print() runs */
          #print-receipt-wrapper { display: none; }
        }
        #print-receipt {
          width: 72mm;
          font-family: ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace;
          font-size: 11px;
          color: #000;
          line-height: 1.35;
          padding: 4px 4px 24px 4px;
          background: white;
        }
        #print-receipt .center { text-align: center; }
        #print-receipt .right { text-align: right; }
        #print-receipt h1 { font-size: 14px; margin: 0; font-weight: 800; letter-spacing: 0.05em; }
        #print-receipt .small { font-size: 10px; }
        #print-receipt .muted { color: #555; }
        #print-receipt hr { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
        #print-receipt table { width: 100%; border-collapse: collapse; font-size: 11px; }
        #print-receipt td { vertical-align: top; padding: 1px 0; }
        #print-receipt .bold { font-weight: 700; }
        #print-receipt .total-row { font-weight: 800; font-size: 12px; }
      `}</style>

      <div id="print-receipt-wrapper">
      <div id="print-receipt" data-testid="print-receipt">
        <div className="center">
          <h1>{branch?.name || "Furmigo"}</h1>
          {branch?.address && <div className="small muted">{branch.address}</div>}
          {branch?.phone && <div className="small muted">{branch.phone}</div>}
          {branch?.gst_no && <div className="small muted">GSTIN: {branch.gst_no}</div>}
        </div>
        <hr />
        <table>
          <tbody>
            <tr><td className="bold">Bill</td><td className="right bold">{invoice.invoice_no}</td></tr>
            <tr><td className="small muted">Date</td><td className="right small muted">{dt}</td></tr>
            {invoice.customer?.name && (
              <tr><td className="small">Customer</td><td className="right small">{invoice.customer.name}</td></tr>
            )}
            {invoice.customer?.mobile_masked && (
              <tr><td className="small muted">Mobile</td><td className="right small muted">{invoice.customer.mobile_masked}</td></tr>
            )}
          </tbody>
        </table>
        <hr />
        <table>
          <thead>
            <tr>
              <td className="bold" style={{ width: "55%" }}>Item</td>
              <td className="bold right" style={{ width: "12%" }}>Qty</td>
              <td className="bold right" style={{ width: "33%" }}>Amount</td>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.lid || it.ref_id || i}>
                <td>
                  <div style={{ wordBreak: "break-word" }}>{it.name}</div>
                  {it.unit_price !== undefined && (
                    <div className="small muted">@ {inr(it.unit_price)}</div>
                  )}
                </td>
                <td className="right">{it.qty}</td>
                <td className="right">{inr(it.final_total ?? it.line_total ?? (it.unit_price * it.qty))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <hr />
        <table>
          <tbody>
            <tr><td>Subtotal</td><td className="right">{inr(invoice.subtotal)}</td></tr>
            {!!invoice.line_discount_amount && (
              <tr><td>Item discount</td><td className="right">- {inr(invoice.line_discount_amount)}</td></tr>
            )}
            {!!invoice.discount_amount && (
              <tr><td>Bill discount</td><td className="right">- {inr(invoice.discount_amount)}</td></tr>
            )}
            {!!invoice.tax_amount && (
              <tr><td>GST</td><td className="right">{inr(invoice.tax_amount)}</td></tr>
            )}
            {!!invoice.wallet_redeemed && (
              <tr><td>Wallet used</td><td className="right">- {inr(invoice.wallet_redeemed)}</td></tr>
            )}
            <tr className="total-row"><td>TOTAL</td><td className="right">{inr(invoice.grand_total)}</td></tr>
            <tr><td>Paid</td><td className="right">{inr(invoice.paid_total)}</td></tr>
            {!!invoice.balance && (
              <tr className="bold"><td>Balance</td><td className="right">{inr(invoice.balance)}</td></tr>
            )}
          </tbody>
        </table>
        {invoice.payments?.length > 0 && (
          <>
            <hr />
            <div className="small muted bold">Payment</div>
            {invoice.payments.map((p, i) => (
              <div key={i} className="small muted">
                {p.method?.toUpperCase()} · {inr(p.amount)}
                {p.reference && ` · ${p.reference}`}
              </div>
            ))}
          </>
        )}
        <hr />
        <div className="center small">
          <div>Thank you for shopping with Furmigo!</div>
          <div className="muted">furmigo.shop</div>
          {invoice.billed_by_name && <div className="muted">Billed by: {invoice.billed_by_name}</div>}
        </div>
      </div>
      </div>
    </>
  );
}
